/*
 *  TTTech nerve-management-system
 *  Copyright(c) 2023. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import BaseApiService from './base.api-service';
import { extractFilenameFromHeader } from '@/helpers/file-download';

const PATH = '/nerve/dna';
const SERVICE_PATH = '/nerve/service-os-dna';

class DnaApiService extends BaseApiService {
  getWorkloadTargetConfiguration(param) {
    const options = {
      cancelToken: param.token,
    };
    return this.get(`${PATH}/${param.serialNumber}/status`, options, { authRequired: true });
  }

  async downloadWorkloadCurrentConfiguration(serialNumber) {
    const response = await this.fetchWithHeaders({
      method: 'get',
      url: `${PATH}/${serialNumber}/current`,
      headers: {
        Accept: 'application/zip; application/octet-stream',
      },
      responseType: 'blob',
      authRequired: true,
    });

    return {
      data: response.data,
      filename: `workload-dna-currentconf-${serialNumber}.zip`,
    };
  }

  async downloadWorkloadTargetConfiguration(serialNumber) {
    const response = await this.fetchWithHeaders({
      method: 'get',
      url: `${PATH}/${serialNumber}/target`,
      headers: {
        Accept: 'application/zip; application/octet-stream',
      },
      responseType: 'blob',
      authRequired: true,
    });

    return {
      data: response.data,
      filename: `workload-dna-targetconf-${serialNumber}.zip`,
    };
  }

  reApplyWorkloadTargetConfiguration(payload) {
    const queryParams = new URLSearchParams(payload.queryParams).toString();
    const queryString = queryParams ? `?${queryParams}` : '';
    return this.put(`${PATH}/${payload.serialNumber}/target/re-apply${queryString}`, {}, { authRequired: true });
  }

  applyWorkloadConfiguration(payload) {
    const queryParams = new URLSearchParams(payload.queryParams).toString();
    const queryString = queryParams ? `?${queryParams}` : '';
    return this.put(`${PATH}/${payload.serialNumber}/target${queryString}`, payload.formData, {
      authRequired: true,
    });
  }

  cancelApplyWorkloadConfiguration(serialNumber) {
    return this.patch(`${PATH}/${serialNumber}/target/cancel`, {}, { authRequired: true });
  }

  getNodeTargetConfiguration(param) {
    const options = {
      cancelToken: param.token,
    };
    return this.get(`${SERVICE_PATH}/${param.serialNumber}/status`, options, { authRequired: true });
  }

  /* Node (service-os) DNA routes - mirror workload DNA methods */
  async downloadNodeCurrentConfiguration(serialNumber) {
    const response = await this.fetchWithHeaders({
      method: 'get',
      url: `${SERVICE_PATH}/${serialNumber}/current`,
      headers: {
        Accept: 'application/x-yaml; application/octet-stream',
      },
      responseType: 'blob',
      authRequired: true,
    });

    return {
      data: response.data,
      filename:
        extractFilenameFromHeader(response.headers['content-disposition']) ||
        `node-dna-currentconf-${serialNumber}.yaml`,
    };
  }

  async downloadNodeTargetConfiguration(serialNumber) {
    const response = await this.fetchWithHeaders({
      method: 'get',
      url: `${SERVICE_PATH}/${serialNumber}/target`,
      headers: {
        Accept: 'application/zip; application/octet-stream',
      },
      responseType: 'blob',
      authRequired: true,
    });

    return {
      data: response.data,
      filename:
        extractFilenameFromHeader(response.headers['content-disposition']) ||
        `node-dna-targetconf-${serialNumber}.yaml`,
    };
  }

  reApplyNodeTargetConfiguration(payload) {
    return this.put(
      `${SERVICE_PATH}/${payload.serialNumber}/target/re-apply`,
      {},
      {
        authRequired: true,
      },
    );
  }

  applyNodeConfiguration(payload) {
    return this.put(`${SERVICE_PATH}/${payload.serialNumber}/target`, payload.formData, {
      authRequired: true,
    });
  }
}

export default new DnaApiService();
